import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";

export function StandardQuestionDeleteMenu() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionId, setQuestionId] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get("/admin/standard-questions");
        if (!cancelled) {
          const rows = response.data?.questions || [];
          setQuestions(rows);
          setQuestionId((current) => current || rows[0]?.question_id || "");
        }
      } catch (ex) {
        if (!cancelled) toast.error(formatApiErrorDetail(ex));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selected = useMemo(
    () => questions.find((q) => q.question_id === questionId) || null,
    [questions, questionId],
  );

  const close = () => {
    if (deleting) return;
    setOpen(false);
    setQuestionId("");
  };

  const deleteSelected = async () => {
    if (!selected || deleting) return;

    const confirmed = window.confirm(
      `Permanently delete this standard question?\n\n${selected.text}\n\n` +
      "This removes the question and any saved Nova/Echo audio. This cannot be undone.",
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await api.delete(
        `/admin/standard-questions/${encodeURIComponent(selected.question_id)}/permanent`,
      );
      toast.success("Standard question permanently deleted.");
      setOpen(false);
      setQuestionId("");
      window.location.reload();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      setDeleting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full border border-red-300 px-4 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
        data-testid="admin-delete-standard-question"
      >
        Delete Question
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/35 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-standard-question-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div className="w-full max-w-xl rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="delete-standard-question-title" className="text-lg font-semibold text-[color:var(--text-primary)]">
                  Delete standard question
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-[color:var(--text-secondary)]">
                  Permanent deletion is for setup mistakes or unused questions. Archive a question instead when you want to preserve it for history.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={deleting}
                className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] disabled:opacity-50"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <label className="mt-5 block">
              <span className="text-xs font-medium text-[color:var(--text-primary)]">Question to delete</span>
              <select
                value={questionId}
                onChange={(event) => setQuestionId(event.target.value)}
                disabled={loading || deleting || questions.length === 0}
                className="mt-1 w-full rounded-xl border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                data-testid="delete-standard-question-select"
              >
                {questions.length === 0 && <option value="">No questions available</option>}
                {questions.map((q) => (
                  <option key={q.question_id} value={q.question_id}>
                    {q.active === false ? "[Archived] " : ""}{q.text}
                  </option>
                ))}
              </select>
            </label>

            {selected && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-relaxed text-red-900">
                <div className="font-semibold">This action cannot be undone.</div>
                <div className="mt-1">Deleting also removes any pre-generated Female and Male audio stored with this question.</div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                disabled={deleting}
                className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-xs font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteSelected}
                disabled={!selected || loading || deleting}
                className="rounded-full bg-red-700 px-4 py-2 text-xs font-semibold text-white hover:bg-red-800 disabled:opacity-40"
                data-testid="delete-standard-question-confirm"
              >
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
